import { useDispatch, useSelector } from "react-redux";
import { TranslationObject } from "../../../../models/translationModal";
import {
    chatMessagesSelector,
    chatMessagesNotAuthSelector,
    isSupportCountMsgMaxSelector,
} from "../../../../store/support/support.selectors";
import {
    util as chatUtil,
    endpoints as chatEndpoints,
} from "../../../../store/support/support.api";
import {
    getOrCreateAnonymousUserId,
    loadAnonymousSupportMessages,
    saveAnonymousSupportMessages,
} from "../../../../store/support/anonymousSupportChatSession";
import { sortSupportMessages } from "../../../../store/support/supportMessageSort";
import { MutableRefObject, useEffect, useLayoutEffect, useRef, useState } from "react";
import _ from "lodash";
import { useActions } from "../../../../hooks/actions";
import Echo from "laravel-echo";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import io from "socket.io-client";
import "moment/locale/ru";
import moment from "moment";
import { useGetDirectoriesQuery } from "../../../../store/directories/directories.api";
import {
    IChatSupportMessage,
    IMessagePayload,
    ISupportMessage,
} from "../../../../models/chatModels";
import { getCurrentLanguage } from "../../../../store/directories/selectors";

const SUPPORT_CHAT_BAN_MESSAGE_CODE = "chat_ai_spam_ban_message";

const isSupportBanBotMessage = (message: ISupportMessage): boolean =>
    message.code === SUPPORT_CHAT_BAN_MESSAGE_CODE
    || message.message === SUPPORT_CHAT_BAN_MESSAGE_CODE;

const isSupportIncomingMessage = (message: ISupportMessage): boolean =>
    Boolean(message.bot_response || message.admin_id || message.kommo_operator);

const isSupportUserMessage = (message: ISupportMessage): boolean =>
    !isSupportIncomingMessage(message);

const resolveSupportMessageText = (
    message: ISupportMessage,
    entities?: TranslationObject,
): string => {
    if (isSupportBanBotMessage(message)) {
        return entities?.chat_ai_spam_ban_message?.value
            ?? message.message
            ?? message.text
            ?? "";
    }

    return message.message ?? message.text ?? "";
};

const isOpenAiSupportBotMessage = (message: ISupportMessage): boolean =>
    Boolean(message.bot_response) && !message.admin_id && !isSupportBanBotMessage(message);

interface UseGetChatMessagesData {
    chatId?: string | number;
    token: string;
    userId?: string | number;
    role?: string;
    chatRef: MutableRefObject<HTMLDivElement | null>;
    entities?: TranslationObject;
    isSupport?: boolean;
}

// TODO: тяжело читаемый хук, нужно рефакторить
export const useGetSupportMessages = ({
    chatId,
    userId,
    token,
    role,
    chatRef,
    entities,
    isSupport = false,
}: UseGetChatMessagesData) => {
    const { data: directoriesData } = useGetDirectoriesQuery();

    const {
        setSupportMessages,
        addSupportMessage,
        addSupportMessageNotAuth,
        addSupportMessages,
        resetSupportState,
        addSupportMessagesNotAuth,
        setSupportChatBanned,
    } = useActions();

    const currentLanguage = getCurrentLanguage();

    const [, setAnonymousUserId] = useState<string | null>(null);

    const supportMessages = useSelector(chatMessagesSelector);

    const chatMessagesNotAuth = useSelector(chatMessagesNotAuthSelector);

    const isSupportCountMsgMax = useSelector(isSupportCountMsgMaxSelector);

    const nextCursor = useRef<string | null>(null);

    const isCursorFetching = useRef<boolean>(false);

    const newCursorMessages = useRef<boolean>(false);

    /** Не скроллить в конец после подгрузки истории вверх (пагинация) — позицию выставляет другой эффект */
    const skipScrollToBottomForPagination = useRef<boolean>(false);

    const chatScrollHeight = useRef<number | null>(null);

    const guestSessionPersistReady = useRef(false);

    const dispatch = useDispatch();

    const [
        getChatMessages,
        { data: chatMessagesData, isSuccess: isSuccessChatMessages },
    ] = chatEndpoints.getChatMessages.useLazyQuery();

    const scrollToBottomChat = () => {
        if (!chatRef.current) return;

        chatRef.current.scrollTo({
            top: chatRef.current.scrollHeight,
            behavior: "smooth",
        });
    };

    const getFormaterMessages = (
        messages: ISupportMessage[],
    ): IChatSupportMessage[] => {
        const sortedMessages = sortSupportMessages(messages);

        return _.reduce(
            sortedMessages,
            (acc: IChatSupportMessage[], curr: ISupportMessage): IChatSupportMessage[] => {
                const dataMessageDate = moment
                    .utc(curr?.created_at)
                    .local()
                    .locale("ru")
                    .format("D MMMM");

                if (curr.payload) {
                    curr = updateTextVariables(curr, entities);
                }

                if (acc.find((el) => el.date === dataMessageDate)) {
                    return acc.map((el) => {
                        if (el.date === dataMessageDate) {
                            return {
                                ...el,
                                messages: [...el.messages, curr],
                            };
                        }
                        return el;
                    });
                }

                return [
                    ...acc,
                    { date: dataMessageDate, messages: [curr] },
                ];
            },
            [],
        );
    };

    const updateSupMsg = (ms: ISupportMessage[]) => {
        const messages = ms.reduce((total: ISupportMessage[], item: ISupportMessage) => {
            if (item.bot_system) {
                total.push({
                    ...item,
                    id: item.id ?? -1,
                    is_your: false,
                    text: "",
                    created_at: item.created_at ?? new Date().toISOString(),
                });

                return total;
            }

            if (item.user_message) {
                total.push({
                    ...item,
                    is_your: true,
                    text: item.user_message,
                });
            }

            if (item.bot_response !== null && item.bot_response !== undefined && item.bot_response !== "") {
                total.push({
                    ...item,
                    is_your: false,
                    text: String(item.bot_response),
                });
            }

            return total;
        }, []);

        if (messages.length) {
            setSupportMessages(getFormaterMessages(messages));
        } else {
            setSupportMessages([]);
        }
    };

    useEffect(() => {
        return () => {
            nextCursor.current = null;
            newCursorMessages.current = false;
            chatScrollHeight.current = null;
            guestSessionPersistReady.current = false;
            resetSupportState();
        };
    }, []);

    useLayoutEffect(() => {
        if (userId) {
            guestSessionPersistReady.current = false;
            setSupportMessages([]);

            return;
        }

        const anonymousId = getOrCreateAnonymousUserId();
        const storedMessages = loadAnonymousSupportMessages(anonymousId);

        if (storedMessages.length > 0) {
            addSupportMessagesNotAuth(storedMessages);
        }

        guestSessionPersistReady.current = true;
    }, [userId]);

    useEffect(() => {
        if (userId || !guestSessionPersistReady.current) {
            return;
        }

        saveAnonymousSupportMessages(
            getOrCreateAnonymousUserId(),
            chatMessagesNotAuth,
        );
    }, [chatMessagesNotAuth, userId]);

    useEffect(() => {
        if (userId) {
            return;
        }

        const displayMessages = isSupportCountMsgMax
            ? [
                ...chatMessagesNotAuth,
                {
                    id: -1,
                    bot_system: true,
                    bot_response: "",
                    created_at: new Date().toISOString(),
                } as ISupportMessage,
            ]
            : chatMessagesNotAuth;

        updateSupMsg(displayMessages);
    }, [chatMessagesNotAuth, isSupportCountMsgMax, userId]);

    useEffect(() => {
        if (chatId && userId) {
            const requestData = {
                chatId, params: {
                    per_page: 15, page: 1,
                },
            };
            getChatMessages(requestData);
        }
    }, [chatId, currentLanguage]);

    useEffect(() => {
        if (!chatId || !userId) {
            return;
        }

        const refetch = (): void => {
            if (document.visibilityState !== "visible") {
                return;
            }
            getChatMessages({
                chatId,
                params: { per_page: 15, page: 1 },
            });
        };

        document.addEventListener("visibilitychange", refetch);
        return () => document.removeEventListener("visibilitychange", refetch);
    }, [chatId, userId, getChatMessages]);

    useEffect(() => {
        if (userId) {
            const serverMessages = (chatMessagesData?.data || [])
                .filter((msg): msg is ISupportMessage => Boolean(msg))
                .map(msg => ({
                    ...msg,
                    is_your: msg.is_your ?? isSupportUserMessage(msg),
                    text: resolveSupportMessageText(msg, entities),
                    message: resolveSupportMessageText(msg, entities),
                }));

            if (serverMessages.some(isSupportBanBotMessage)) {
                setSupportChatBanned(true);
            }

            const optimisticMessages = supportMessages
                .flatMap((group) => group.messages)
                .filter((message) => message.id < 0 && !message.bot_system)
                .filter((optimisticMessage) =>
                    !serverMessages.some((serverMessage) =>
                        serverMessage.id === optimisticMessage.id
                        || (
                            serverMessage.is_your
                            && optimisticMessage.is_your
                            && serverMessage.message === optimisticMessage.message
                        )
                    )
                );

            const messages = [
                ...serverMessages,
                ...optimisticMessages,
            ];

            if (messages?.length) {
                const formatChatMessages = getFormaterMessages(messages);
                if (nextCursor.current && isCursorFetching.current) {
                    skipScrollToBottomForPagination.current = true;
                    addSupportMessages(formatChatMessages);
                    isCursorFetching.current = false;
                    newCursorMessages.current = true;
                } else {
                    setSupportMessages(formatChatMessages);
                }
            } else if (isSuccessChatMessages && chatMessagesData) {
                setSupportMessages([]);
            }

            nextCursor.current = chatMessagesData?.next_cursor ?? null;
        }
    }, [chatMessagesData, isSuccessChatMessages]);

    useEffect(() => {

        if (!userId) {
            const storedUserId = getOrCreateAnonymousUserId();

            setAnonymousUserId(storedUserId);

            const client = new Echo({
                broadcaster: `socket.io`,
                host: directoriesData?.echo_server?.host,
                transports: [`websocket`],
                client: io
            });

            client
                .channel(`chats.anonymous.${storedUserId}`)
                .listen(
                    `.chat_message_was_sent`,
                    function (socketMessage: ISupportMessage) {
                        if (!socketMessage?.bot_response) {
                            return;
                        }

                        if (isSupport && isOpenAiSupportBotMessage(socketMessage)) {
                            return;
                        }

                        addSupportMessageNotAuth({
                            ...socketMessage,
                            bot_response: socketMessage.message,
                        });

                        setTimeout(() => {
                            scrollToBottomChat();
                        }, 500);
                    }
                );

        }

        if (
            chatId &&
            userId &&
            token &&
            role &&
            directoriesData?.echo_server?.host
        ) {
            const client = new Echo({
                broadcaster: `socket.io`,
                host: directoriesData?.echo_server?.host,
                transports: [`websocket`],
                client: io,
                auth: {
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                },
            });

            client
                .private(`chats.${chatId}.${role}.${userId}`)
                .listen(
                    `.chat_message_was_sent`,
                    function (socketMessage: ISupportMessage) {

                        if (socketMessage) {
                            const isOwnUserMessage = isSupportUserMessage(socketMessage);

                            if (isOwnUserMessage) {
                                return;
                            }

                            if (isSupport && isOpenAiSupportBotMessage(socketMessage)) {
                                return;
                            }

                            if (isSupportBanBotMessage(socketMessage)) {
                                setSupportChatBanned(true);
                            }

                            const resolvedMessageText = resolveSupportMessageText(socketMessage, entities);

                            const socketMessageDate = moment
                                .utc(socketMessage?.created_at)
                                .local()
                                .locale("ru")
                                .format("D MMMM");

                            if (socketMessage.payload) {
                                socketMessage = updateTextVariables(
                                    socketMessage,
                                    entities
                                );
                            }

                            if (
                                socketMessage.code ===
                                "specialist_offers_to_make_a_deal" ||
                                socketMessage.code === "order_offer_reject" ||
                                socketMessage.code ===
                                "customer_stopped_search_specialist"
                            ) {
                                dispatch(chatUtil.invalidateTags(["Support"]));
                            }

                            addSupportMessage({
                                date: socketMessageDate,
                                messages: [{
                                    ...socketMessage,
                                    is_your: socketMessage.is_your ?? isSupportUserMessage(socketMessage),
                                    text: resolvedMessageText,
                                    message: resolvedMessageText,
                                }],
                            });

                            setTimeout(() => {
                                scrollToBottomChat();
                            }, 500);
                        }
                    }
                );

            return () => {
                client.leave(`chats.${chatId}.${role}.${userId}`);
            };
        }
    }, [chatId, userId, token, role, isSupport, directoriesData?.echo_server?.host]);

    useEffect(() => {
        if (
            chatRef.current &&
            supportMessages?.length &&
            !chatScrollHeight.current
        ) {
            chatRef.current.scrollTop = chatRef.current.scrollHeight;
            chatScrollHeight.current = chatRef.current.scrollHeight;
        } else if (
            chatRef.current &&
            supportMessages?.length &&
            chatScrollHeight.current &&
            newCursorMessages.current
        ) {
            chatRef.current.scrollTop =
                chatRef.current.scrollHeight - chatScrollHeight.current;
            chatScrollHeight.current = chatRef.current.scrollHeight;
            newCursorMessages.current = false;
        }
    }, [chatRef, supportMessages, chatScrollHeight, newCursorMessages]);

    useEffect(() => {
        if (!supportMessages.length) {
            return;
        }

        if (skipScrollToBottomForPagination.current) {
            skipScrollToBottomForPagination.current = false;
            return;
        }

        if (newCursorMessages.current || !userId) {
            scrollToBottomChat();
            return;
        }

        // Авторизованный support: новое сообщение / полный refetch списка (без пагинации вверх)
        requestAnimationFrame(() => {
            scrollToBottomChat();
        });
    }, [supportMessages]);

    const supporthandleClickNextCursor = () => {
        if (nextCursor.current && chatId) {
            const requestData = {
                chatId,
                params: {
                    per_page: 15,
                    cursor: nextCursor.current,
                },
            };

            getChatMessages(requestData);

            isCursorFetching.current = true;
        }
    };

    return {
        supportMessages,
        supporthandleClickNextCursor,
    };
};

function updateTextVariables(message: ISupportMessage, entities?: TranslationObject) {
    let text = message.text || message.message;
    const payload = (message.payload ?? {}) as Partial<IMessagePayload>;
    const { email, phone, phone_customer, price, title, avatar, full_name } = payload;

    if (title) {
        text =
            `<p class='title'>${entities?.specialist_contacts?.value}</p>` +
            text;
    }

    if (price) {
        text = text.replace(
            ":price",
            `<span class='highlight'>${price.toString()}</span>`
        );
    }

    if (email) {
        text = text.replace(email, `<span class='highlight'>${email}</span>`);
    }

    if (avatar) {
        text += `<img class='avatar' src='${avatar.original_url}' alt='avatar'/>`;
    }

    if (full_name) {
        text += `<p class='full_name'>${full_name}</p>`;
    }

    if (phone_customer) {
        text = text.replace(phone_customer, `<span class='highlight'>${phone_customer}</span>`);
        text += `<p class='phone'>${phone_customer}</p>`;
    } else if (phone) {
        text = text.replace(phone, `<span class='highlight'>${phone}</span>`);
        text += `<p class='phone'>${phone}</p>`;
    }

    return {
        ...message,
        text,
    };
}
